/*
 * Copyright (c) 2024 Cozy Intellect Inc.
 * File Path: apps/desktop/src/lib/util/assistantMachine.ts
 */

import { goto } from '$app/navigation';
import type { CalendarEvent, Minutes, Transcript, TranscriptFull } from '@repo/common';
import type {
	AbortRecordingEvent,
	AssistantContext,
	AssistantEvents,
	DeleteRecordingEvent,
	DeleteRecordingsEvent,
	DeviceSelectedEvent,
	LoadMediaEvent,
	RecordingStoppedEvent,
	SetEventEvent,
	StartRecordingEvent,
	StartSummarizingEvent,
	StopSummarizingEvent
} from '@repo/common/types/AssistantMachine';

import { checkSetupRequired, setupRequired } from '@repo/sharedlib/src/stores/onboarding.store';
import { getTranscriptSummary, transcripts } from '@repo/sharedlib/src/stores/transcripts';
import { viewer } from '@repo/sharedlib/src/stores/viewer';
import { createStateMachineLogger } from '@repo/sharedlib/src/utils/logger.util';
import { timestampStrToSeconds } from '@repo/toolslib/src/utils/timecode';

import { get } from 'svelte/store';
import { actions, assign, cancel, choose, createMachine, send, type EventObject } from 'xstate4';
import { type AssignAction } from 'xstate4/lib/types';
import {
	cancelTranscribe,
	cancelTranscribeCommand,
	forceCleanup
} from '$lib/stores/transcriber.store';

type ExportDestination = 'notion' | 'obsidian';
type ExportStatus = 'ready' | 'requested' | 'exporting' | 'complete' | 'error';
export type ExportMap = Map<ExportDestination, ExportStatus>;

// Create logger instance
const logger = createStateMachineLogger();

// Define the event type
interface TimeEvent extends EventObject {
	type: string;
	data: {
		time: string;
	};
}

const { log } = actions;

const resetRecording: AssignAction<AssistantContext, EventObject, EventObject> = assign({
	recordProgress: () => 0,
	transcribeProgress: () => 0,
	fullTranscribe: () => false,
	summaryProgress: () => 0,
	transcription: () => [],
	uniqueCaptions: () => new Map<string, string>(),
	summary: () => ({}),
	transcript: () => undefined,
	mergeDataset: () => undefined,
	needsTruncateExisting: false,
	pendingFinalTranscribe: () => false,
	pendingFinalSummarize: () => false,
	isExistingRecording: () => false,
	ffmpegProcess: () => undefined,
	audioDetected: () => undefined,
	calendarEvent: () => undefined,
	recordingPromise: () => undefined
});

const resetEvent: AssignAction<AssistantContext, EventObject, EventObject> = assign({
	calendarEvent: () => undefined
});

export interface TranscribeCompleteResult extends EventObject {
	type: 'TRANSCRIBE_COMPLETE';
	transcript: TranscriptFull | undefined;
	transcription: string[];
	transcribeProgress: number;
	audioDetected: boolean;
	startSummary: boolean;
	incrementalIndex: number;
}

export interface SummaryCompleteResult extends EventObject {
	type: 'SUMMARY_COMPLETE';
	summary: Minutes | string | undefined;
}

export interface ConvertToWavResult extends EventObject {
	type: 'CONVERT_TO_WAV_COMPLETE';
	cancelFinalTranscribe: boolean;
}

const sendStartTranscribing = send((context: AssistantContext) => ({
	type: 'START_TRANSCRIBING',
	transcript: context.transcript!,
	calendarEvent: context.calendarEvent,
	start: context.recordProgress
}));

const sendFullSummarize = send<AssistantContext, StartSummarizingEvent, StartSummarizingEvent>(
	(context) => ({
		type: 'START_SUMMARIZING',
		transcript: context.transcript!,
		calendarEvent: context.calendarEvent,
		start: 0
	}),
	{ delay: (context) => context.summarizeInterval }
);

const updateRecordProgress = assign({
	recordProgress: (_context, event: TimeEvent) => {
		const time = event.data.time;
		return timestampStrToSeconds(time);
	}
});

const updateMemory = assign({
	transcript: (_context, event) => {
		// Clone to prevent shared state with transcripts store
		return event.transcript ? { ...event.transcript } : event.transcript;
	},
	calendarEvent: (context: AssistantContext, event: any) => {
		return (event.calendarEvent || context.calendarEvent) as CalendarEvent;
	}
});

const sendTranscribeIfEnabled = choose([
	{
		cond: (context) => {
			// Only enable transcription if we have a properly initialized file
			// This prevents premature transcription attempts that would fail
			const hasFile = context.rtTranscribe && context.transcript?.file !== undefined;
			const hasTransformedPath = context.transcript?.file?.transformedPath !== undefined;
			return hasFile && hasTransformedPath;
		},
		actions: sendStartTranscribing
	}
]);

// Add a new action for handling summarize requests in onDone context
const checkAndSendSummarize = choose<AssistantContext, any>([
	{
		cond: (_context, event) => event.data.startSummary,
		actions: [sendFullSummarize, 'setSummaryTab']
	}
]);

const logDeviceSelected = log(
	(_context: any, event: DeviceSelectedEvent | StartRecordingEvent | AbortRecordingEvent | any) => {
		if (event.deviceId && event.deviceName) {
			return `Device Selected: ${event.deviceId} - ${event.deviceName}`;
		}
		return `Skipping device selection`;
	}
);

const fileIsDefined = (context: AssistantContext) => {
	const hasTranscript = context.transcript !== undefined;
	const hasFile = context.transcript?.file !== undefined;
	const hasTransformedPath = context.transcript?.file?.transformedPath !== undefined;
	return hasTranscript && hasFile && hasTransformedPath;
};

export const machine = createMachine<AssistantContext, AssistantEvents>(
	{
		id: 'assistantMachine',
		strict: true,
		predictableActionArguments: true,
		// @ts-expect-error Type gen issue
		tsTypes: {} as import('./assistantMachine.typegen').Typegen0,
		schema: {
			context: {} as AssistantContext,
			events: {} as AssistantEvents,
			services: {} as {
				createMedia: {
					data: void;
				};
				loadMedia: {
					data: {
						transcript: Transcript;
					};
				};
				stopRecordProcess: {
					data: void;
				};
				getAvailableDevices: {
					data: void;
				};
				deleteRecordings: {
					data: void;
				};
				warmupModel: {
					data: void;
				};
				transcribeRecording: {
					data: TranscribeCompleteResult;
				};
				finalTranscribe: {
					data: Omit<TranscribeCompleteResult, 'incrementalIndex'>;
				};
				summarize: {
					data: SummaryCompleteResult;
				};
				finalSummarize: {
					data: Omit<SummaryCompleteResult, 'incrementalIndex'>;
				};
				export: {
					data: ExportMap;
				};
				convertToWav: {
					data: ConvertToWavResult;
				};
				cleanUpAll: {
					data: void;
				};
			}
		},
		initial: 'selectFile',
		context: () =>
			({
				label: 'Creating file',
				audioDetected: undefined,
				recordProgress: 0,
				transcribeProgress: 0,
				summaryProgress: 0,
				transcription: [] as string[],
				summary: {} as Partial<Minutes>,
				transcribeInterval: 1000,
				summarizeInterval: 10000,
				uniqueCaptions: new Map<string, string>(),
				deviceId: undefined,
				deviceName: undefined,
				file: undefined,
				exportStatus: new Map<ExportDestination, ExportStatus>(),
				rtTranscribe: true,
				rtSummarize: false,
				mergeDataset: undefined,
				needsTruncateExisting: false,
				fullTranscribe: false,
				pendingFinalTranscribe: false,
				pendingFinalSummarize: false,
				isExistingRecording: false,
				recordingPromise: undefined,
				incrementalIndex: 0
			}) as AssistantContext,
		states: {
			selectFile: {
				id: 'selectFile',
				initial: 'ready',
				states: {
					ready: {
						entry: ['warmupModel'],
						always: [
							{ target: '#assistant', cond: (context) => context?.transcript?.file !== undefined },
							{
								target: 'awaitingSetup',
								cond: () => get(setupRequired)
							}
						],
						on: {
							START_RECORDING: {
								target: ['creating'],
								actions: [
									log(() => {
										logger.info('User started recording');
										return 'User started recording';
									}),
									'warmupModel',
									'forceTranscribeCleanup',
									updateMemory,
									'setActiveTranscript',
									'resetRecordProgress',
									'setDevice'
								]
							},
							DELETE_RECORDING: {
								target: '#selectFile.deleting',
								actions: 'updateDeleteTranscript'
							} as any,
							DELETE_RECORDINGS: {
								target: '#selectFile.deleting',
								actions: 'updateDeleteTranscripts'
							} as any,
							START_TRANSCRIBING: [
								{
									cond: (_context, event) => event.start === -1,
									target: '#assistant.transcriber.finalTranscribing',
									actions: [
										log('Starting final transcription for existing recording'),
										updateMemory,
										'setActiveTranscript',
										assign({
											transcribeProgress: () => -1,
											fullTranscribe: () => true,
											isExistingRecording: () => true // Mark as existing recording
										})
									]
								},
								{
									target: '#assistant.transcriber.transcribing',
									actions: [
										updateMemory,
										'setActiveTranscript',
										assign({
											transcribeProgress: (context, event) => {
												return event.start === -1 ? -1 : context.transcribeProgress;
											},
											fullTranscribe: () => false,
											isExistingRecording: () => false // Mark as existing recording
										})
									]
								}
							],
							START_SUMMARIZING: {
								target: '#assistant.summarizer.summarizing',
								actions: [
									log('Starting summarizing'),
									log((context, event) => {
										console.log('START_SUMMARIZING event received:', {
											event,
											hasTranscript: !!context.transcript,
											transcriptId: context.transcript?.id,
											hasRawOutput: !!context.transcript?.rawOutput?.length,
											rawOutputLength: context.transcript?.rawOutput?.length || 0,
											transcriptStatus: context.transcript?.status
										});
										return 'START_SUMMARIZING debug info logged';
									}),
									updateMemory,
									'setActiveTranscript',
									'setSummaryTab',
									assign({
										summaryProgress: (context, event) =>
											event.start === 0 ? 0 : context.transcribeProgress
									})
								]
							},
							LOAD_MEDIA: {
								target: ['loading', '#assistant.summarizer.ready'],
								actions: [
									resetRecording,
									assign<AssistantContext, LoadMediaEvent>({
										transcript: (_, event) => event.transcript
									})
								]
							} as any,
							SELECT_DEVICE: {
								target: 'loadingAvailableDevices'
							}
						}
					},
					awaitingSetup: {
						always: [
							{
								cond: () => !get(setupRequired),
								target: 'ready'
							}
						],
						on: {
							DELETE_RECORDING: {
								target: '#selectFile.deleting',
								actions: ['updateDeleteTranscript']
							},
							DELETE_RECORDINGS: {
								target: '#selectFile.deleting',
								actions: ['updateDeleteTranscripts']
							}
						},
						after: {
							1000: {
								target: 'awaitingSetup',
								actions: ['checkSetupRequired']
							}
						}
					},
					loadingAvailableDevices: {
						invoke: {
							src: 'getAvailableDevices',
							onDone: {
								target: 'selectingDevice'
							}
						}
					},
					selectingDevice: {
						invoke: {
							src: 'getAvailableDevices'
						},
						on: {
							DEVICE_SELECTED: {
								target: 'ready',
								actions: [logDeviceSelected, 'setDevice']
							}
						}
					},
					creating: {
						invoke: {
							src: 'createMedia',
							onDone: {
								target: '#assistant.recorder.checkDevice',
								cond: (context) => context.transcript?.file !== undefined
							},
							onError: {
								target: 'error',
								actions: [log('Failed to create media file')]
							}
						}
					},
					loading: {
						invoke: {
							src: 'loadMedia',
							onDone: {
								target: '#assistant'
							}
						}
					},
					deleting: {
						onEntry: [log('deleting recordings...'), cancel('START_TRANSCRIBING')],
						invoke: {
							src: 'deleteRecordings',
							onEntry: [log('deleting recordings...')],
							onDone: {
								target: 'ready',
								actions: [log('finished deleting recordings...'), resetRecording, resetEvent]
							}
						}
					},
					error: {
						entry: [resetRecording, cancel('START_TRANSCRIBING')],
						invoke: {
							src: 'cleanUpAll',
							onDone: {
								target: 'ready',
								actions: [log('finished cleaning up...')]
							}
						}
					}
				}
			},
			assistant: {
				id: 'assistant',
				type: 'parallel',
				entry: 'prepareMedia',
				on: {
					EXIT_ASSISTANT: {
						target: '#exiting'
					},
					LOAD_MEDIA: {
						target: ['#selectFile.loading', '#assistant.summarizer.ready'],
						actions: [resetRecording]
					},
					DELETE_RECORDING: {
						target: '#selectFile.deleting',
						actions: ['updateDeleteTranscript']
					},
					DELETE_RECORDINGS: {
						target: '#selectFile.deleting',
						actions: ['updateDeleteTranscripts']
					},
					SET_RT_TRANSCRIBE: {
						actions: [
							assign({
								rtTranscribe: (_, event) => event.value
							}),
							log('setting rtTranscribe')
						]
					},
					SET_RT_SUMMARIZE: {
						actions: log('RT Summarize not available now')
						// TODO: Re-enable rtSummarize when feature is available
						//     assign({
						//     rtSummarize: (_, event) => (event.value)
						// })
					}
				},
				states: {
					recorder: {
						id: 'recorder',
						initial: 'ready',
						states: {
							cleanUp: {
								invoke: {
									src: 'cleanUpAll',
									onDone: {
										target: 'ready'
									}
								}
							},
							ready: {
								always: [
									{
										cond: (context) => !!context.recordProgress,
										target: 'complete'
									}
								],
								on: {
									START_RECORDING: {
										target: '#selectFile.creating',
										actions: [
											'warmupModel',
											updateMemory,
											'setActiveTranscript',
											'resetRecordProgress',
											'setDevice'
										]
									},
									RECORDING_ERROR: {
										actions: [
											assign({
												ffmpegProcess: () => undefined,
												errorMessage: (_context, event) => event.message
											}),
											'showRecorderErrorToast'
										],
										target: 'error'
									},
									RECORDING_WARNING: {
										actions: ['showRecorderWarningToast']
									},
									DEVICE_SELECTED: {
										internal: true,
										actions: [logDeviceSelected, 'setDevice']
									},
									DELETE_RECORDING: {
										target: '#selectFile.deleting',
										actions: ['updateDeleteTranscript']
									},
									DELETE_RECORDINGS: {
										target: '#selectFile.deleting',
										actions: ['updateDeleteTranscripts']
									}
								}
							},
							// First state pending recording: decide whether we need to load available devices
							checkDevice: {
								always: [
									{ target: 'pendingRecord', cond: (ctx) => ctx.deviceName !== undefined },
									{ target: 'loadingAvailableDevices' }
								]
							},
							loadingAvailableDevices: {
								invoke: {
									src: 'getAvailableDevices',
									onDone: {
										target: 'pendingRecord'
									}
								}
							},
							pendingRecord: {
								id: 'pendingRecord',
								always: [
									{ target: 'spawning', cond: (context) => context.deviceName !== undefined }
								],
								on: {
									DEVICE_SELECTED: {
										actions: [logDeviceSelected, 'setDevice']
									}
								}
							},
							spawning: {
								entry: [
									log('spawning record'),
									'spawnRecordProcess' // Start the process but don't wait for it
								],
								always: [
									{
										cond: (context) => !context.transcript?.file,
										target: 'error',
										actions: [log('No transcript file available for recording')]
									}
								],
								// Transition immediately after spawning - don't wait for Promise resolution
								after: {
									RECORDING_STARTUP_TIMEOUT: {
										target: 'error',
										actions: [
											log('Recording startup timeout - transitioning to error state'),
											assign({
												ffmpegProcess: (_context) => undefined
											})
										]
									}
								},
								on: {
									STOP_RECORDING: {
										target: 'stopping',
										actions: [log((_context, event) => `Stopped recording - ${event.reason}`)]
									},
									// If recording progress is received, transition to awaitingFile
									RECORDING_PROGRESS: {
										target: 'awaitingFile',
										actions: [updateRecordProgress] as any
									},
									RECORDING_ERROR: {
										actions: assign({
											ffmpegProcess: () => undefined,
											errorMessage: (_context, event) => event.message
										}),
										target: 'error'
									},
									RECORDING_WARNING: {
										actions: ['showRecorderWarningToast']
									},
									RECORDING_STOPPED: {
										target: 'error',
										actions: [
											log(
												(_context, event: RecordingStoppedEvent) =>
													`Recording stopped unexpectedly - ${event.reason}`
											)
										]
									},
									ABORT_RECORDING: {
										target: 'aborting',
										actions: [log('Aborting recording')]
									}
									// Note: RECORDING_PROGRESS is intentionally ignored here
									// until the file is available and we transition to 'recording' state
								}
							},
							awaitingFile: {
								entry: log('awaiting file to be available before starting recording'),
								always: [
									{
										cond: (context) => {
											return fileIsDefined(context);
										},
										target: 'recording',
										actions: [log('File is now available, starting recording')]
									}
								],
								on: {
									STOP_RECORDING: {
										target: 'stopping',
										actions: [log((_context, event) => `Stopped recording - ${event.reason}`)]
									},
									RECORDING_PROGRESS: {
										internal: true,
										actions: [updateRecordProgress] as any
									},
									RECORDING_WARNING: {
										actions: ['showRecorderWarningToast']
									},
									RECORDING_STOPPED: {
										target: 'error',
										actions: [
											log(
												(_context, event: RecordingStoppedEvent) =>
													`Recording stopped unexpectedly - ${event.reason}`
											)
										]
									},
									ABORT_RECORDING: {
										target: 'aborting',
										actions: [log('Aborting recording')]
									}
									// Note: RECORDING_PROGRESS is intentionally ignored here
									// until the file is available and we transition to 'recording' state
								},
								after: {
									RECORDING_STARTUP_TIMEOUT: {
										target: 'error',
										actions: [
											log('Awaiting file timeout - transitioning to error state'),
											assign({
												ffmpegProcess: () => undefined
											})
										]
									}
								}
							},
							recording: {
								always: [
									{
										cond: (context) => !context.transcript?.file,
										target: 'error',
										actions: [log('No transcript file available during recording')]
									}
								],
								on: {
									STOP_RECORDING: {
										target: 'stopping',
										actions: [
											log((_context, event) => {
												logger.info('User stopped recording', { reason: event.reason });
												return `User stopped recording - ${event.reason}`;
											}),
											assign({ pendingFinalTranscribe: (_context) => true })
										]
									},
									ABORT_RECORDING: {
										target: 'aborting',
										actions: [
											log(() => {
												logger.info('User aborted recording');
												return 'User aborted recording';
											})
										]
									},
									RECORDING_PROGRESS: {
										internal: true,
										actions: [updateRecordProgress, sendTranscribeIfEnabled] as any
									},
									RECORDING_ERROR: {
										actions: assign({
											ffmpegProcess: () => undefined,
											errorMessage: (_context, event) => event.message
										}),
										target: 'error'
									},
									RECORDING_WARNING: {
										actions: ['showRecorderWarningToast']
									}
								}
							},
							stopping: {
								invoke: {
									src: 'stopRecordProcess',
									onDone: [
										{
											cond: (context) => !!context.pendingFinalTranscribe,
											target: 'convertingWav'
										},
										{
											target: 'complete'
										}
									],
									onError: {
										target: 'complete',
										actions: [log('stop error'), assign({ ffmpegProcess: undefined })]
									}
								},
								on: {
									RECORDING_STOPPED: {
										// Ignore RECORDING_STOPPED events in stopping state - they're expected
										actions: [log('Recording stopped (expected in stopping state)')]
									}
								}
							},
							aborting: {
								invoke: {
									src: 'stopRecordProcess',
									onDone: {
										target: 'complete',
										actions: [
											log('aborting...'),
											assign({
												ffmpegProcess: undefined,
												pendingFinalTranscribe: false,
												pendingFinalSummarize: false
											})
										]
									},
									onError: {
										target: 'complete',
										actions: [
											log('stop error'),
											assign({
												ffmpegProcess: undefined,
												pendingFinalTranscribe: false,
												pendingFinalSummarize: false
											})
										]
									}
								}
							},
							convertingWav: {
								on: {
									ABORT_RECORDING: {
										target: 'complete',
										actions: [
											log('canceled converting wav'),
											assign({
												pendingFinalTranscribe: () => false,
												pendingFinalSummarize: () => false
											})
										]
									},
									STOP_RECORDING: {
										target: 'complete',
										actions: [
											log('canceled converting wav'),
											assign({
												pendingFinalTranscribe: () => false,
												pendingFinalSummarize: () => false
											})
										]
									}
								},
								invoke: {
									src: 'convertToWav',
									onDone: {
										target: 'complete',
										actions: assign({
											pendingFinalTranscribe: (context, event) =>
												event.data.cancelFinalTranscribe ? false : context.pendingFinalTranscribe
										})
									},
									onError: {
										target: 'complete',
										actions: [
											log('WAV conversion error'),
											assign({ pendingFinalTranscribe: false })
										]
									}
								}
							},
							complete: {
								on: {
									START_RECORDING: {
										target: '#selectFile.creating',
										actions: [
											'warmupModel',
											'resetRecording',
											'resetRecordProgress',
											updateMemory,
											'setActiveTranscript',
											'resetTranscribeProgress',
											logDeviceSelected,
											'setDevice'
										]
									},
									DELETE_RECORDING: {
										target: '#selectFile.deleting',
										actions: ['updateDeleteTranscript']
									},
									DELETE_RECORDINGS: {
										target: '#selectFile.deleting',
										actions: ['updateDeleteTranscripts']
									},
									SELECT_DEVICE: {
										target: '#selectFile.selectingDevice'
									}
								}
							},
							error: {
								after: {
									RECORDER_ERROR_TIMEOUT: {
										// Wait MINUTES_UPDATE_DELAY ms before accepting new transcription requests
										target: 'cleanUp',
										actions: [log('Error timeout, resetting to cleanUp')]
									}
								},
								on: {
									START_RECORDING: {
										target: '#selectFile.creating',
										actions: [
											'warmupModel',
											resetRecording,
											'resetRecordProgress',
											updateMemory,
											'setActiveTranscript',
											'resetTranscribeProgress',
											logDeviceSelected,
											'setDevice'
										]
									},
									DELETE_RECORDING: {
										target: '#selectFile.deleting',
										actions: ['updateDeleteTranscript']
									},
									DELETE_RECORDINGS: {
										target: '#selectFile.deleting',
										actions: ['updateDeleteTranscripts']
									},
									SELECT_DEVICE: {
										target: '#selectFile.selectingDevice'
									}
								}
							}
						}
					},
					transcriber: {
						id: 'transcriber',
						initial: 'ready',
						states: {
							ready: {
								always: [
									{
										cond: (context) => !!context.transcribeProgress,
										target: 'complete'
									},
									{
										cond: (context) => context.pendingFinalTranscribe,
										target: 'finalTranscribing',
										actions: [log('Starting final transcription from pending')]
									}
								],
								on: {
									START_TRANSCRIBING: [
										{
											cond: (context, event) => event.start === -1 && fileIsDefined(context),
											target: 'finalTranscribing',
											actions: [
												log('Starting final transcription for existing recording'),
												updateMemory,
												'setActiveTranscript',
												assign({
													transcribeProgress: () => -1,
													fullTranscribe: () => true,
													isExistingRecording: () => true // Mark as existing recording
												})
											]
										},
										{
											cond: (context) => {
												// Check if we have a properly initialized transcript for transcription
												return fileIsDefined(context);
											},
											target: 'transcribing',
											actions: [
												log('received start transcribing (from ready)'),
												updateMemory,
												'setActiveTranscript',
												assign({
													transcribeProgress: (context, event) => {
														return event.start === -1 ? -1 : context.transcribeProgress;
													},
													fullTranscribe: () => false,
													isExistingRecording: () => false // Mark as existing recording
												})
											]
										},
										{
											// If file is not ready, stay in ready state and log the issue
											actions: [
												log('Cannot start transcription - file not ready yet'),
												log(
													(context) =>
														`File status: transcript=${!!context.transcript}, file=${!!context.transcript?.file}, transformedPath=${!!context.transcript?.file?.transformedPath}`
												)
											]
										}
									],
									TRANSCRIBE_ERROR: { target: 'error' },
									WAV_CONVERSION_COMPLETE: {
										// This is for when the user chooses "retranscribe"
										target: 'finalTranscribing', // Go directly to final transcription without adding pending flag
										actions: [
											log('Starting final transcription'),
											assign({ isExistingRecording: false }) // Reset for normal flow
										]
									}
								}
							},
							transcribing: {
								always: [
									{
										cond: (context) => !fileIsDefined(context),
										target: 'error',
										actions: [
											(context) =>
												log(`No file available for transcription: ${context.transcript?.file}`)
										]
									}
								],
								entry: log(() => {
									logger.info('Started transcription');
									return 'starting transcription';
								}),
								invoke: {
									src: 'transcribeRecording',
									onDone: {
										target: 'dedupe',
										actions: [
											log((_context, event) => {
												logger.info('Transcription completed', {
													progress: event.data.transcribeProgress,
													audioDetected: event.data.audioDetected
												});
												return 'Transcription completed';
											}),
											assign({
												transcript: (_context, event) => {
													console.log('[DEBUG] Assigning transcript with status:', event.data.transcript?.status);
													return event.data.transcript;
												},
												transcribeProgress: (_context, event) => {
													console.log(
														`Finished transcribing, setting transcribeProgress: ${event.data.transcribeProgress}`
													);
													return event.data.transcribeProgress;
												},
												audioDetected: (_context, event) => {
													return event.data.audioDetected;
												},
												incrementalIndex: (_context, event) => {
													return event.data.incrementalIndex;
												},
												fullTranscribe: (_context) => false,
												isExistingRecording: (_context) => false // Reset for normal flow
											}),
											checkAndSendSummarize
										]
									},
									onError: {
										target: 'error'
									}
								},
								on: {
									TRANSCRIPT_PROGRESS: {
										internal: true,
										actions: assign({
											transcript: (_context, event) => {
												// Merge partial transcript updates while keeping status as 'transcribing'
												return {
													..._context.transcript,
													...event.partialTranscript,
													status: 'transcribing'
												} as TranscriptFull;
											}
										})
									},
									STOP_RECORDING: {
										target: 'stopping',
										actions: [
											log((_context, event) => `Stopped recording - ${event.reason}`),
											assign({ pendingFinalTranscribe: (_context) => true, pendingFinalSummarize: (_context) => true })
										]
									},
									ABORT_RECORDING: {
										// No need to wait for the transcription to finish since we are discarding
										target: 'canceling',
										actions: [
											log('Cancelling transcription due to abort recording'),
											assign({ pendingFinalTranscribe: false, pendingFinalSummarize: false })
										]
									},
									STOP_TRANSCRIBING: {
										target: 'canceling',
										actions: [
											assign({ pendingFinalTranscribe: (_context) => false }),
											log((_context, event) => `Stopped transcribing - ${event.reason}`)
										]
									},
									TRANSCRIBE_ERROR: { target: 'error' }
								}
							},
							stopping: {
								invoke: {
									src: 'stopTranscription',
									onDone: {
										target: 'complete',
										actions: [log('Transcription stopped'), 'clearTranscribeLocks']
									}
								},
								on: {
									RECORDING_STOPPED: {
										// Ignore RECORDING_STOPPED events in stopping state - they're expected
										actions: [log('Recording stopped (expected in stopping state)')]
									},
									ABORT_RECORDING: {
										// No need to wait for the transcription to finish since we are discarding
										target: 'canceling',
										actions: [
											log('Cancelling transcription due to abort recording'),
											assign({ pendingFinalTranscribe: false, pendingFinalSummarize: false })
										]
									},
									FINAL_TRANSCRIPTION_COMPLETE: [
										{
											cond: (context, event) =>
												!!(
													context.transcript?.id && context.transcript?.id !== event?.transcriptId
												),
											target: 'ready',
											actions: [log('ignoring irrelevant FINAL_TRANSCRIPTION_COMPLETE event')]
										},
										{
											target: 'complete',
											actions: [
												log('Final transcription complete'),
												'clearTranscribeLocks',
												assign({ pendingFinalTranscribe: false })
											]
										}
									]
								}
							},
							canceling: {
								invoke: {
									src: 'cancelTranscription',
									onEntry: [
										assign({
											pendingFinalTranscribe: (_context) => false,
											pendingFinalSummarize: (_context) => false
										})
									],
									onDone: {
										target: 'canceled',
										actions: [log('Transcription cancelled'), 'clearTranscribeLocks']
									}
								},
								after: {
									CANCEL_INVOKE_TIMEOUT: {
										target: 'complete'
									}
								}
							},
							dedupe: {
								always: {
									// Jump straight to complete if we started transcribing now that the command auto loops
									cond: (context) => context.transcribeProgress > 0,
									actions: [log('Transcription complete')],
									target: 'complete'
								},
								after: {
									TRANSCRIBE_DELAY: {
										// Wait before accepting new transcription requests, but go back to ready for continuous transcription
										target: 'complete'
									}
								},
								on: {
									STOP_RECORDING: {
										target: 'stopping',
										actions: [
											log((_context, event) => `Stopped recording - ${event.reason}`),
											assign({ pendingFinalTranscribe: (_context) => true, pendingFinalSummarize: (_context) => true })
										]
									},
									ABORT_RECORDING: {
										// No need to wait for the transcription to finish since we are discarding
										target: 'canceling',
										actions: [log('Cancelling transcription due to abort recording')]
									},
									STOP_TRANSCRIBING: {
										// Called when user stops the final transcription
										target: 'stopping',
										actions: [
											log((_context, event) => `Stopped transcribing - ${event.reason}`),
											assign({ pendingFinalTranscribe: false })
										]
									},
									FINAL_TRANSCRIPTION_COMPLETE: [
										{
											cond: (context, event) =>
												!!(
													context.transcript?.id && context.transcript?.id !== event?.transcriptId
												),
											target: 'ready',
											actions: [log('ignoring irrelevant FINAL_TRANSCRIPTION_COMPLETE event')]
										},
										{
											target: 'complete',
											actions: [
												log('Final transcription complete'),
												'clearTranscribeLocks',
												assign({ pendingFinalTranscribe: false })
											]
										}
									]
								}
							},
							complete: {
								// entry: log((context) => (['transcription:', context.transcription])),
								always: [
									{
										cond: (context) => {
											// Check if we have a properly initialized file for pending final transcription
											const hasPendingFlag = context.pendingFinalTranscribe;
											const hasFile = !!context.transcript?.file;
											const hasTransformedPath = !!context.transcript?.file?.transformedPath;
											console.log('hasPendingFlag', hasPendingFlag);
											console.log('hasFile', hasFile);
											console.log('hasTransformedPath', hasTransformedPath);
											return hasPendingFlag && hasFile && hasTransformedPath;
										},
										target: 'finalTranscribing',
										actions: [log('Starting final transcription from pending'), 'cancelTranscribe']
									}
								],
								on: {
									START_TRANSCRIBING: [
										{
											cond: (context, event) => event.start === -1 && fileIsDefined(context),
											target: 'finalTranscribing',
											actions: [
												log('Starting final transcription for existing recording'),
												updateMemory,
												'setActiveTranscript',
												assign({
													transcribeProgress: () => -1,
													fullTranscribe: () => true,
													isExistingRecording: () => true // Mark as existing recording
												})
											]
										},
										{
											cond: (context) => {
												// Check if we have a properly initialized transcript for transcription
												return fileIsDefined(context);
											},
											target: 'transcribing',
											actions: [
												log('received start transcribing (from ready)'),
												updateMemory,
												'setActiveTranscript',
												assign({
													transcribeProgress: (context, event) => {
														return event.start === -1 ? -1 : context.transcribeProgress;
													},
													fullTranscribe: () => false,
													isExistingRecording: () => false // Mark as existing recording
												})
											]
										},
										{
											// If file is not ready, stay in ready state and log the issue
											actions: [
												log('Cannot start transcription - file not ready yet'),
												log(
													(context) =>
														`File status: transcript=${!!context.transcript}, file=${!!context.transcript?.file}, transformedPath=${!!context.transcript?.file?.transformedPath}`
												)
											]
										}
									],
									ABORT_RECORDING: {
										target: 'canceled'
									}
								}
							},
							canceled: {
								entry: [
									assign({ pendingFinalTranscribe: false, pendingFinalSummarize: false }),
									'notifyUserCanceled'
								],
								after: {
									CANCEL_TIMEOUT: {
										target: '#selectFile.ready'
									}
								}
							},
							finalTranscribing: {
								always: [
									{
										cond: (context) => {
											// Check if we have a properly initialized file for final transcription
											const hasFile = context.transcript?.file !== undefined;
											const hasTransformedPath =
												context.transcript?.file?.transformedPath !== undefined;
											return !hasFile || !hasTransformedPath;
										},
										target: 'complete',
										actions: [
											log(
												'No properly initialized transcript file available, skipping final transcription'
											)
										]
									}
								],
								entry: log(() => {
									logger.info('Started final transcription');
									return 'Started final transcription';
								}),
								invoke: {
									src: 'finalTranscribe',
									onDone: {
										target: 'complete',
										actions: [
											log((_context, event) => {
												logger.info('Final transcription complete', {
													transcript: event.data.transcript,
													progress: event.data.transcribeProgress,
													audioDetected: event.data.audioDetected
												});
												return 'Final transcription complete';
											}),
											assign({
												transcript: (_context, event) => event.data.transcript,
												transcription: (_context, event) => event.data.transcription,
												transcribeProgress: (_context, event) => event.data.transcribeProgress,
												audioDetected: (_context, event) => event.data.audioDetected,
												fullTranscribe: (_context) => false,
												pendingFinalTranscribe: (_context) => false
											})
										]
									},
									onError: {
										target: 'error'
									}
								},
								on: {
									TRANSCRIPT_PROGRESS: {
										internal: true,
										actions: assign({
											transcript: (_context, event) => {
												// Merge partial transcript updates while keeping status as 'transcribing'
												return {
													..._context.transcript,
													...event.partialTranscript,
													status: 'transcribing'
												} as TranscriptFull;
											}
										})
									},
									ABORT_RECORDING: {
										// Hypothetical case
										target: 'canceling',
										actions: [
											log('Cancelling final transcription due to abort recording'),
											'cancelTranscribe',
											assign({
												pendingFinalTranscribe: () => false,
												pendingFinalSummarize: () => false
											})
										]
									},
									STOP_TRANSCRIBING: {
										// The main case is when user stops the final transcription
										target: 'canceling',
										actions: [
											log((_context, event) => `Stopped final transcribing - ${event.reason}`),
											'cancelTranscribe',
											assign({
												pendingFinalTranscribe: (_context) => false,
												pendingFinalSummarize: (_context) => false
											})
										]
									},
									FINAL_TRANSCRIPTION_COMPLETE: [
										{
											cond: (context, event) =>
												!!(
													context.transcript?.id && context.transcript?.id !== event?.transcriptId
												),
											target: 'ready',
											actions: [log('ignoring irrelevant FINAL_TRANSCRIPTION_COMPLETE event')]
										},
										{
											target: 'complete',
											actions: [
												log('Final transcription complete'),
												'clearTranscribeLocks',
												assign({
													pendingFinalTranscribe: () => false,
													pendingFinalSummarize: () => false
												})
											]
										}
									]
								}
							},
							error: {
								after: {
									ERROR_TIMEOUT: {
										// Wait MINUTES_UPDATE_DELAY ms before accepting new transcription requests
										target: 'ready'
									}
								}
							}
						}
					},
					summarizer: {
						id: 'summarizer',
						initial: 'ready',
						states: {
							ready: {
								always: {
									cond: (context) =>
										!!Object.keys(context?.summary || ({} as Partial<Minutes>)).length,
									target: 'complete'
								},
								on: {
									START_SUMMARIZING: {
										target: 'summarizing',
										actions: [
											updateMemory,
											'setActiveTranscript',
											'setSummaryTab',
											assign({
												summaryProgress: (context, event) =>
													event.start === 0 ? 0 : context.transcribeProgress
											})
										]
									},
									FINAL_TRANSCRIPTION_COMPLETE: [
										{
											cond: (context, event) =>
												!!(
													context.transcript?.id && context.transcript?.id !== event?.transcriptId
												),
											actions: [log('ignoring irrelevant FINAL_TRANSCRIPTION_COMPLETE event')]
										},
										{
											cond: (context) => context.isExistingRecording,
											target: 'ready',
											actions: [
												log('Skipping automatic summarization for existing recording'),
												'clearTranscribeLocks'
											]
										},
										{
											target: 'finalSummarizing',
											actions: [
												log('Starting final summarization from ready state'),
												log((_context) => `Summarizer context: ${JSON.stringify(_context.summary)}`),
												'clearTranscribeLocks'
											]
										}
									],
									SUMMARY_ERROR: { target: 'error' }
								}
							},
							summarizing: {
								// Remove the always condition that skips summarization for empty rawOutput
								// Let the summarize service handle the case where rawOutput is empty
								// This allows the service to provide proper error messages
								entry: log(() => {
									logger.info('Started summarization');
									return 'Started summarization';
								}),
								invoke: {
									src: 'summarize',
									onDone: {
										target: 'complete',
										actions: [
											log(() => {
												logger.info('Summarization completed');
												return 'Summarization completed';
											}),
											assign({ pendingFinalSummarize: false, pendingFinalTranscribe: false })
										]
									},
									onError: {
										target: 'error',
										actions: [
											log((_context, event) => {
												logger.error('Summarization failed', { error: event.data });
												return 'Summarization failed';
											}),
											assign({ pendingFinalSummarize: (_context) => false, pendingFinalTranscribe: (_context) => false }),
											'clearTranscribeLocks'
										]
									}
								},
								on: {
									STOP_SUMMARIZING: {
										target: 'complete',
										actions: ['stopSummarizing', log('received stop summarizing')]
									},
									SUMMARY_ERROR: { target: 'error' }
								}
							},
							complete: {
								on: {
									START_SUMMARIZING: {
										target: 'summarizing',
										actions: [
											updateMemory,
											'setActiveTranscript',
											'setSummaryTab',
											'refreshSidebar',
											assign({
												summaryProgress: (context, event) =>
													event.start === 0 ? 0 : context.transcribeProgress
											})
										],
										cond: (context) => !!context?.transcript?.rawOutput?.length
									},
									FINAL_TRANSCRIPTION_COMPLETE: [
										{
											cond: (context) => context.isExistingRecording,
											target: 'complete',
											actions: [
												log('Skipping automatic summarization for existing recording'),
												'clearTranscribeLocks'
											]
										},
										{
											target: 'finalSummarizing',
											actions: [
												log('Starting final summarization from complete state'),
												log((context) => `Summarizer context: ${JSON.stringify(context.summary)}`),
												'clearTranscribeLocks'
											]
										}
									]
								}
							},
							finalSummarizing: {
								entry: [
									'setSummaryTab',
									log(() => {
										logger.info('Started final summarization');
										return 'Started final summarization';
									})
								],
								on: {
									STOP_SUMMARIZING: {
										target: 'complete',
										actions: [
											'stopSummarizing',
											log(() => {
												logger.info('User stopped final summarization');
												return 'received stop summarizing in finalSummarizing';
											})
										]
									}
								},
								invoke: {
									src: 'finalSummarize',
									onDone: {
										target: 'complete',
										actions: [
											log(() => {
												logger.info('Final summarization completed - user got summary');
												return 'Final summarization completed';
											}),
											(ctx, event) => {
												console.log('finalSummarizing onDone', ctx, event);
											},
											assign({
												summary: (_context, event) => event.data.summary
											})
										]
									},
									onError: {
										target: 'error'
									}
								}
							},
							error: {
								after: {
									ERROR_TIMEOUT: {
										// Wait MINUTES_UPDATE_DELAY ms before accepting new transcription requests
										target: 'ready'
									}
								}
							}
						}
					},
					exporter: {
						id: 'exporter',
						initial: 'ready',
						states: {
							ready: {
								on: {
									START_EXPORTING: [
										{
											target: 'exporting',
											actions: assign({
												exportStatus: (_context, event) => {
													_context.exportStatus.set(event.destination, 'requested');
													return _context.exportStatus;
												}
											})
										}
									],
									EXPORT_ERROR: { target: 'error' }
								}
							},
							exporting: {
								invoke: {
									src: 'export',
									onDone: {
										target: 'complete',
										actions: assign({
											exportStatus: (_context, event) => event.data
										})
									}
								},
								on: {
									STOP_EXPORTING: { target: 'complete' },
									EXPORT_ERROR: { target: 'error' }
								}
							},
							complete: {
								on: {
									START_EXPORTING: [
										{
											target: 'exporting',
											actions: assign({
												exportStatus: (_context, event) => {
													_context.exportStatus.set(event.destination, 'requested');
													return _context.exportStatus;
												}
											})
										}
									]
								}
							},
							error: {
								on: {
									START_EXPORTING: [
										{
											target: 'exporting',
											actions: assign({
												exportStatus: (_context, event) => {
													_context.exportStatus.set(event.destination, 'requested');
													return _context.exportStatus;
												}
											})
										}
									]
								}
							}
						}
					},
					updater: {
						id: 'updater',
						initial: 'ready',
						states: {
							ready: {
								on: {
									START_UPDATING: {
										actions: 'updateMinutes',
										target: 'dedupeUpdate'
									}
								}
							},
							dedupeUpdate: {
								after: {
									MINUTES_UPDATE_DELAY: {
										// Wait MINUTES_UPDATE_DELAY ms before accepting new transcription requests
										target: 'ready'
									}
								}
							}
						}
					},
					eventViewer: {
						id: 'eventViewer',
						initial: 'eventPending',
						states: {
							eventPending: {
								on: {
									SET_EVENT: {
										target: '#eventViewer.eventReady',
										actions: [
											log((_, event) => ['SET_EVENT:', event]) as any,
											assign({
												calendarEvent: (context, event: SetEventEvent) =>
													event.calendarEvent || context.calendarEvent
											})
										]
									}
								}
							},
							eventReady: {
								always: {
									target: 'eventPending',
									cond: (context) => !context.calendarEvent
								},
								on: {
									CLEAR_EVENT: {
										target: '#eventViewer.eventPending',
										actions: [log('Got clear event'), resetEvent]
									}
								}
							}
						}
					}
				}
			},
			editor: {
				id: 'editor',
				always: [
					{ target: 'selectFile', cond: (context) => context.transcript?.file === undefined },
					{
						target: 'assistant',
						cond: (context) => !context.transcription.length
					}
				]
			},
			exiting: {
				id: 'exiting',
				invoke: {
					src: 'stopRecordProcess',
					onDone: {
						target: 'selectFile',
						actions: [log('exiting...'), 'clearTranscript', 'clearEvent']
					}
				}
			}
		}
	},
	{
		actions: {
			prepareMedia: (context) => {
				console.log('Preparing file', context.transcript?.file);
			},
			warmupModel: () => {
				console.log('warmupModel');
			},
			cancelTranscribe: (context) => {
				context.abortController?.abort('User cancelled transcription');
				console.log('canceling final transcription');
				cancelTranscribeCommand();
			},
			forceTranscribeCleanup: () => {
				forceCleanup();
			},
			notifyUserCanceled: () => { },
			checkSetupRequired: () => {
				console.log('Checking setup required...', get(setupRequired));
				checkSetupRequired();
			},
			stopSummarizing: (_context, event: StopSummarizingEvent) => {
				console.log('Stopping summarizing', event.reason);
			},
			resetRecordProgress: (context) => {
				context.recordProgress = 0;
			},
			deleteTranscription: (context) => {
				context.transcription = [];
			},
			deleteSummary: (context) => {
				context.transcription = [];
			},
			setDevice: (context, event: DeviceSelectedEvent | StartRecordingEvent) => {
				if (event.deviceName && event.deviceId) {
					context.deviceName = event.deviceName;
					context.deviceId = event.deviceId;
				}
			},
			spawnRecordProcess: async (_context) => {
				// This action will be implemented in assistant.ts
				// It starts the recording process and stores the Promise in context
				console.log('spawnRecordProcess action called');
			},
			setSummaryTab: (context) => {
				// Only navigate if we're not in a test environment
				// Check for test environment using NODE_ENV or vitest globals
				const isTest = typeof process !== 'undefined' && process.env?.NODE_ENV === 'test';
				const isVitestTest = typeof globalThis !== 'undefined' && 'vi' in globalThis;
				console.log('setSummaryTab');

				if (typeof window !== 'undefined' && !isTest && !isVitestTest) {
					// Update viewer store to reflect the tab change
					viewer.update((v) => ({ ...v, noteTab: 'summary' }));
					// Navigate to the summary tab
					goto(`/app/docs/${context.transcript?.id}/summary`);
				} else {
					console.log('setSummaryTab: skipping navigation in test environment');
				}
			},
			clearTranscript: (context) => {
				context.transcript = undefined;
			},
			clearEvent: (context) => {
				context.calendarEvent = undefined;
			},
			updateMinutes: () => { },
			setActiveTranscript: () => { },
			resetTranscribeProgress: () => { },
			refreshSidebar: () => { },
			updateDeleteTranscript: assign({
				transcript: (context, event: DeleteRecordingEvent) => {
					const transcriptId = event.id;
					console.log('deleting recording...', context, event);
					const transcript = getTranscriptSummary(transcriptId);
					// Note: We only need the summary for display in delete dialog
					// The actual deletion uses transcriptIds, not the full transcript
					if (transcript) {
						return transcript as any; // Cast as TranscriptFull for type compatibility
					}
					return undefined;
				}
			}),
			updateDeleteTranscripts: assign({
				transcript: (context, event: DeleteRecordingsEvent) => {
					const transcriptIds = event.ids;
					console.log('deleting recordings...', context, event);
					// For batch deletion, we'll use the first transcript as the primary one
					// The actual deletion logic will handle all IDs
					const transcript = get(transcripts).idMap.get(transcriptIds[0]);
					// Note: We only need the summary for display in delete dialog
					if (transcript) {
						return transcript as any; // Cast as TranscriptFull for type compatibility
					}
					return undefined;
				},
				transcriptIds: (_context, event: DeleteRecordingsEvent) => {
					return event.ids;
				}
			}),
			resetRecording: resetRecording,
			showRecorderErrorToast: (_context, _event) => {
				// Send a custom event that the parent can listen to
				// The parent component should handle showing the toast
				console.log('Recording error occurred - should show toast');
			},
			showRecorderWarningToast: (_context, event) => {
				console.warn('Recording warning:', (event as { message?: string }).message);
			},
			clearTranscribeLocks: () => {
				forceCleanup();
			}
		},
		services: {
			warmupModel: async () => {
				console.log('warmupModel');
			},
			cleanUpAll: async () => {
				console.log('cleanUpAll');
			},
			createMedia: () => {
				return async () => {
					console.log('Created media');
				};
			},
			stopTranscription: async () => {
				console.log('Stopping transcription...');
				try {
					// Cancel using the AbortController method
					const canceled = cancelTranscribe('User stopped transcription');
					if (canceled) {
						console.log('Transcription stopped via AbortController');
					}

					// Also try the direct Rust command as backup
					await cancelTranscribeCommand();
					console.log('Transcription stopped via Rust command');
				} catch (error) {
					console.debug('Error stopping transcription:', error);
				}
			},
			cancelTranscription: async () => {
				console.log('Canceling transcription...');
				try {
					// Cancel using the AbortController method
					const canceled = cancelTranscribe('User cancelled transcription');
					if (canceled) {
						console.log('Transcription cancelled via AbortController');
					}

					// Also try the direct Rust command as backup
					await cancelTranscribeCommand();
					console.log('Transcription cancelled via Rust command');
				} catch (error) {
					console.error('Error cancelling transcription:', error);
				}
			},
			loadMedia: (context, event: LoadMediaEvent) => {
				return async () => {
					const { transcript } = event;

					// Set the new transcript and its data
					context.transcript = transcript;
					context.recordProgress = transcript.duration || 0;
					context.transcribeProgress = transcript.duration || 0;
					context.transcription = [...(transcript.editedOutput || [])];
					if (transcript.meeting?.minutes) {
						context.summary = transcript.meeting.minutes as Partial<Minutes>;
					}
					console.log('Loaded media');
				};
			},
			stopRecordProcess: (context) => {
				return async () => {
					console.log('stopRecordProcess');
					context.ffmpegProcess = undefined;
				};
			},
			getAvailableDevices: () => {
				return async () => {
					console.log('getAvailableDevices');
				};
			},
			deleteRecordings: () => {
				return async () => {
					// Dummy that is replaced by implementation in assistant.ts
					console.log('deleting file...');
					return;
				};
			},
			transcribeRecording: async (context): Promise<TranscribeCompleteResult> => {
				// This service should be properly implemented to pass context to the actual function
				// For now, let's log what we're receiving to debug the context issue
				console.log('State machine transcribeRecording service called with context:', {
					hasTranscript: !!context.transcript,
					hasFile: !!context.transcript?.file,
					hasTransformedPath: !!context.transcript?.file?.transformedPath,
					transcriptId: context.transcript?.id,
					filePath: context.transcript?.file?.transformedPath
				});

				// The actual implementation should be here, but for debugging we'll return a mock result
				return {
					transcribeProgress: context.transcribeProgress || 0,
					transcript: context.transcript || ({} as TranscriptFull),
					transcription: context.transcription || [],
					audioDetected: false,
					type: 'TRANSCRIBE_COMPLETE',
					startSummary: false,
					incrementalIndex: context.incrementalIndex
				};
			},
			summarize: async (): Promise<SummaryCompleteResult> => {
				return {
					type: 'SUMMARY_COMPLETE',
					summary: undefined
				};
			},
			export: () => {
				return async () => {
					return {} as ExportMap;
				};
			},
			convertToWav: async (): Promise<ConvertToWavResult> => {
				console.log('Converting to WAV...');
				// This will be implemented in assistant.ts to call convertMediaFile
				return {
					type: 'CONVERT_TO_WAV_COMPLETE',
					cancelFinalTranscribe: false
				};
			},
			finalTranscribe: async (
				context
			): Promise<Omit<TranscribeCompleteResult, 'incrementalIndex'>> => {
				console.log('Final transcription...');
				return {
					type: 'TRANSCRIBE_COMPLETE',
					transcript: context.transcript,
					transcription: [],
					transcribeProgress: 0,
					audioDetected: false,
					startSummary: false
				};
			},
			finalSummarize: async (): Promise<SummaryCompleteResult> => {
				console.log('Final summarization...');
				// Note: Toast should be handled by the parent component
				// when it receives the SUMMARY_COMPLETE event
				return {
					type: 'SUMMARY_COMPLETE',
					summary: 'Final summary'
				};
			}
		},
		delays: {
			TRANSCRIBE_DELAY: (context) => {
				return context.transcribeInterval;
			},
			MINUTES_UPDATE_DELAY: () => {
				return 500;
			},
			ERROR_TIMEOUT: () => {
				return 2000;
			},
			RECORDER_ERROR_TIMEOUT: () => {
				return 1000;
			},
			RECORDING_STARTUP_TIMEOUT: () => {
				return 30000;
			},
			CANCEL_INVOKE_TIMEOUT: () => {
				return 5000;
			},
			CANCEL_TIMEOUT: () => {
				return 1000;
			}
		}
	}
);
